import axios from "axios";
import { recordRequestError } from "./requestDiagnostics";
import { clearSession, refreshSession } from "@/services/auth/authService";
import {
  finishNetworkRequest,
  finishNetworkRetry,
  startNetworkRequest,
  startNetworkRetry,
  normalizeRequestError,
} from "@/services/http/api";

const baseURL = process.env.VUE_APP_BASEURL || "/api";

/**
 * Cliente HTTP autenticado. Agrega el header Authorization automáticamente
 * leyendo el token de localStorage (guardado por authService).
 */
const apiAuth = axios.create({
  baseURL,
  timeout: 20000,
});

apiAuth.interceptors.request.use((config) => {
  startNetworkRequest();
  config._tracksGlobalLoading = true;
  const token = localStorage.getItem("bochas_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const RETRY_DELAY = 1500;
const BACKOFF_FACTOR = 1.5;
const MAX_RETRY_DELAY = 10000;

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function shouldRetry(error) {
  if (axios.isCancel(error)) return false;
  if (!["get", "head"].includes((error.config?.method || "get").toLowerCase())) return false;
  if (!error.response) return true;
  const status = error.response.status;
  // Reintentamos 5xx, pero NO 401 (token inválido)
  return [502, 503, 504].includes(status);
}

apiAuth.interceptors.response.use(
  (response) => {
    if (response.config?._tracksGlobalLoading) {
      response.config._tracksGlobalLoading = false;
      finishNetworkRequest();
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    if (!axios.isCancel(error)) recordRequestError({
      url: originalRequest?.url,
      method: originalRequest?.method,
      status: error.response?.status || 0,
      code: error.code || "HTTP_ERROR",
      retry: originalRequest?._retryCount || 0,
    });
    if (originalRequest?._tracksGlobalLoading) {
      originalRequest._tracksGlobalLoading = false;
      finishNetworkRequest();
    }
    if (!originalRequest) {
      return Promise.reject(normalizeRequestError(error));
    }
    if (error.response?.status === 401) {
      // Token expirado o inválido → limpiamos sesión
      if (!originalRequest._refreshRetry && (await refreshSession())) {
        originalRequest._refreshRetry = true;
        originalRequest.headers.Authorization = `Bearer ${localStorage.getItem(
          "bochas_token",
        )}`;
        return apiAuth(originalRequest);
      }
      clearSession();
      return Promise.reject(error);
    }
    originalRequest._retryCount = originalRequest._retryCount || 0;

    if (shouldRetry(error)) {
      originalRequest._retryCount++;
      const delay = Math.min(
        RETRY_DELAY * Math.pow(BACKOFF_FACTOR, originalRequest._retryCount),
        MAX_RETRY_DELAY,
      );
      startNetworkRetry();
      try {
        await sleep(delay);
        return await apiAuth(originalRequest);
      } finally {
        finishNetworkRetry();
      }
    }
    return Promise.reject(normalizeRequestError(error));
  },
);

export default apiAuth;
